import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Page } from '../../shared/models/page';
import { MatchingAgency } from '../models/matching-agency';
import { Observable } from 'rxjs/Observable';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { LogService } from '../../shared/log/log.service';
import { TranslateService } from '@ngx-translate/core';
import { MatchingAgencyCandidate } from '../models/matching-agency-candidate';
import { MatchingAgencyHttpService } from './matching-agency.http.service';
import { Constants } from '../../shared/constants';
import { PagesizeType } from './pagination/pagesize-type';
import { UserLocalStorageService } from '../../permission/services/user.local.storage.service';
import { AuthenticationStore } from '../../permission/services/authentication.store';
import { MatchingAgencyCandidateAssign } from '../models/matching-agency-candidate-assign';
import { MatchingAgencyAdd } from '../models/matching-agency-add';

@Injectable()
export class MatchingAgencyStore {

  private matchingAgencySubject: BehaviorSubject<Page<MatchingAgency>> = new BehaviorSubject(Page.empty<MatchingAgency>());

  constructor(
    private matchingAgencyHttpService: MatchingAgencyHttpService,
    private flashMessagesService: FlashMessagesService,
    private logService: LogService,
    private translateService: TranslateService,
    private userLocalStorageService: UserLocalStorageService,
    private authenticationStore: AuthenticationStore
  ) {
    this.init();
  }

  private init(): void {
    if (this.authenticationStore.isSupportUser()) {
      this.getMatches(
        Constants.SEARCH_DEFAULT_PAGE,
        this.userLocalStorageService.getPagesize(PagesizeType.MATCHING_AGENCY),
        Constants.DEFAULT_MATCHING_AGENCY_FIELD_SORT_FIELD,
        null,
        Constants.SORT_DIRECTION_ASC
      ).take(1).subscribe();
    }
  }

  get matches(): Observable<Page<MatchingAgency>> {
    return this.matchingAgencySubject.asObservable();
  }

  get count(): Observable<number> {
    return this.matchingAgencySubject.asObservable().map(page => page.totalElements);
  }

  getMatches(page: number, size: number, sortField: string, secondarySortField: string, sortDirection: string): Observable<any> {
    let sort = 'sort=' + sortField + ',' + sortDirection;
    if (secondarySortField) {
      sort = sort + '&sort=' + secondarySortField + ',' + sortDirection;
    }

    return this.matchingAgencyHttpService.getMatches(page, size, sort).catch(error => {
      this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.GET_MATCHING_AGENCIES'));
      this.logService.error(`Error getting Matching Agencies: ${error}`);
      return Observable.throw(error);
    }).map(agencyMatches => {
      this.matchingAgencySubject.next(agencyMatches);
      return agencyMatches;
    });
  }

  assignCandidate(candidateAssign: MatchingAgencyCandidateAssign): Observable<any> {
    return this.matchingAgencyHttpService.assign(candidateAssign)
      .catch(error => {
        this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.ASSIGN_MATCHING_AGENCY'));
        this.logService.error(`Error assigning Matching Agency: ${error}`);
        return Observable.throw(error);
      })
      .map(result => {
        this.flashMessagesService.success('EVENT.AGENCY_REVIEW.CANDIDATE_ASSIGNED');
        return result;
      });
  }

  addAgency(newRec: MatchingAgencyAdd): Observable<MatchingAgency> {
    return this.matchingAgencyHttpService.add(newRec)
      .catch(error => {
        this.flashMessagesService.error('MESSAGES.ERROR.ADD_AGENCY', `Error adding agency: ${error}`);
        return Observable.throw(error);
      })
      .map((res: MatchingAgency) => {
        this.flashMessagesService.success('MESSAGES.SUCCESS.ADD_AGENCY_MATCHING');
        return res;
      });
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/matching-agency.store.ts